/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type {
  Color,
  HallowElement,
  HallowElementProps,
  TextProps,
  ThemeType,
} from "@packages/ui";
import {
  Carousel,
  colors,
  cssMediaQueryMinWidthTablet,
  QuoteIcon,
  radius,
  spacing,
  Text,
  useCarousel,
  useIsDesktopViewport,
  useThemeValues,
} from "@packages/ui";
import { format, isSameYear, parse } from "date-fns";

const containerStyles: Interpolation = {
  paddingBottom: spacing.m,
};

const carouselContainerStyles: Interpolation = {
  alignItems: "center",
};

const cardStyles = (props: { backgroundColor: string }): Interpolation => ({
  alignItems: "flex-start",
  backgroundColor: props.backgroundColor,
  borderRadius: radius.m,
  color: colors.black,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  height: spacing.full,
  justifyContent: "space-between",
  padding: spacing.l,
  width: spacing.full,
});

const cardDesktopStyles: Interpolation = {
  alignItems: "center",
  borderRadius: radius.ms,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  height: "420px",
  justifyContent: "space-between",
  padding: spacing.xxl,
  textAlign: "center",
  width: spacing.full,
};

const messageBoxStyles: Interpolation = {
  maxWidth: "800px",
};

const iconContainerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
};

const dateStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedHigh,
});

const mobileContainerStyles: Interpolation = {
  height: "420px",
};

const attributionStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  rowGap: spacing.t,
  alignItems: "flex-start",
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "center",
  },
};

const Element: HallowElement = "div";

type TestimonialsProps = HallowElementProps<typeof Element> & {
  css?: Interpolation<ThemeType>;
};

type Testimonial = {
  message: string;
  attribution?: string;
  date?: Date;
  color?: Color;
};

const testimonials: Testimonial[] = [
  {
    message: "web_homepage_quote_devotional_message",
    attribution: "web_homepage_quote_devotional_attribution",
    color: "#F4DEFF",
    date: parse("2025-01-04", "yyyy-MM-dd", new Date()),
  },
  {
    message: "web_homepage_quote_depression_message",
    attribution: "web_homepage_quote_depression_attribution",
    color: "#FFF4AC",
    date: parse("2024-12-12", "yyyy-MM-dd", new Date()),
  },
  {
    message: "web_homepage_quote_structure_message",
    attribution: "web_homepage_quote_structure_attribution",
    color: "#C9DB75",
    date: parse("2024-12-19", "yyyy-MM-dd", new Date()),
  },
  {
    message: "web_homepage_quote_companion_message",
    attribution: "web_homepage_quote_companion_attribution",
    color: "#DBF0FA",
    date: parse("2024-12-31", "yyyy-MM-dd", new Date()),
  },
  {
    message: "web_homepage_quote_sleep_message",
    attribution: "web_homepage_quote_sleep_attribution",
    color: "#F4DEFF",
    date: parse("2024-09-05", "yyyy-MM-dd", new Date()),
  },
  {
    message: "web_homepage_quote_transform_message",
    attribution: "web_homepage_quote_transform_attribution",
    color: "#FFF4AC",
    date: parse("2024-08-08", "yyyy-MM-dd", new Date()),
  },
  {
    message: "web_homepage_quote_circumstances_message",
    attribution: "web_homepage_quote_circumstances_attribution",
    color: "#C9DB75",
    date: parse("2024-07-31", "yyyy-MM-dd", new Date()),
  },
];

export const Testimonials = ({ css: cssProp }: TestimonialsProps) => {
  const theme = useThemeValues();
  const t = useTranslations();
  const isDesktop = useIsDesktopViewport();
  const carouselApi = useCarousel({
    size: "xl",
    fixedWidth: true,
  });

  const messageProps: Partial<TextProps> = isDesktop
    ? { type: "headline", size: "l" }
    : { type: "title", size: "xl" };

  return (
    <Carousel
      carouselApi={carouselApi}
      css={[containerStyles, cssProp]}
      containerCSS={[
        carouselContainerStyles,
        !isDesktop && mobileContainerStyles,
      ]}
    >
      {testimonials.map((testimonial, index) => {
        return (
          <div
            key={`testimonialDesktop-${index}`}
            css={[
              cardStyles({
                backgroundColor:
                  testimonial.color ?? theme.semanticColors.neutralsLowest,
              }),
              isDesktop && cardDesktopStyles,
            ]}
          >
            <span key="iconContainer" css={iconContainerStyles}>
              <QuoteIcon />
            </span>
            <Text
              as="p"
              {...messageProps}
              css={messageBoxStyles}
              title={t(testimonial.message)}
              lineClamp={isDesktop ? 6 : 11}
            >
              {t(testimonial.message)}
            </Text>
            <div key={"attribution"} css={attributionStyles}>
              <Text as="p" type="body" size="l">
                {t(testimonial.attribution) ??
                  t("quote_general_hallow_user_attribution")}
              </Text>
              {testimonial.date && (
                <Text as={"span"} type={"body"} size={"s"} css={dateStyles}>
                  {format(
                    testimonial.date,
                    isSameYear(testimonial.date, new Date())
                      ? "LLL d"
                      : "LLL d, yyyy",
                  )}
                </Text>
              )}
            </div>
          </div>
        );
      })}
    </Carousel>
  );
};

Testimonials.displayName = "Testimonials";
